import {
    ApiQueryEndpoints,
    ApiQuery,
    QueryResponse,
    FilterMetadata,
} from "~/modules/filters/types";
import {
    ApiQueryFetcher,
    ApiQueryStateManager,
    ApiSearchLayout,
} from "~/modules/filters/components/ApiSearchLayout";
import { ExportTableNames } from "~/modules/downloads/types";
import { SearchControlModes } from "~/modules/filters/components/FilterControls/types";
import { Asset, AssetColumns } from "~/modules/assets/types";
import { AssetTable } from "~/modules/assets/components/AssetTable";
import { ColumnMetadata } from "~/components/DataGrid";
import { AssetTableRowData } from "~/modules/assets/components/AssetTable/types";
import { ClassnamePassthruWrapper } from "~/components/ui/classname-passthru-wrapper";
import { cn } from "~/lib/utils";

// // // //

export function AssetList(props: {
    initialState: QueryResponse<Asset>;
    assetsQuery: ApiQuery;
    filterMetadata: FilterMetadata[];
    title: string;
    analyticsTableName: string;
    columns: ColumnMetadata<AssetTableRowData, AssetColumns>[];
    bordered?: boolean;
    fixedHeader?: boolean;
    fixedHeaderClassName?: string;
    enableExport?: boolean;
    exportFilename?: string;
    enableApiTools?: boolean;
    enableBasicSearch?: boolean;
    enableProSearch?: boolean;
    includeAdditionalNetworksRow?: boolean;
}) {
    const {
        assetsQuery,
        initialState,
        filterMetadata,
        title,
        analyticsTableName,
        columns,
        bordered,
        fixedHeader = true,
        fixedHeaderClassName,
        enableExport = true,
        exportFilename,
        enableApiTools = true,
        enableBasicSearch = true,
        enableProSearch = false,
        includeAdditionalNetworksRow,
    } = props;

    return (
        <ClassnamePassthruWrapper
            className={cn({ "mt-6": enableExport || enableApiTools })}
        >
            <ApiQueryStateManager
                defaultFilters={assetsQuery}
                modifyApiQuery={(apiQuery) => {
                    // Use structuredClone for deep cloning apiQuery + assetsQuery.filter
                    // Prevents mutation of the original apiQuery object
                    const clonedQuery = structuredClone(apiQuery);
                    if (!clonedQuery.filter) {
                        clonedQuery.filter = structuredClone(
                            assetsQuery.filter,
                        );
                    }

                    return clonedQuery;
                }}
            >
                {({ appliedQuery, internalQuery, resetQuery }) => (
                    <ApiQueryFetcher
                        appliedQuery={appliedQuery.query}
                        internalQuery={internalQuery.query}
                        endpoint={ApiQueryEndpoints.AssetV4}
                        initialState={initialState}
                    >
                        {(state) => {
                            return (
                                <ApiSearchLayout
                                    title={title}
                                    filterMetadata={filterMetadata}
                                    onResetQuery={() => {
                                        resetQuery();
                                    }}
                                    appliedQuery={appliedQuery}
                                    internalQuery={internalQuery}
                                    queryFetcherState={state}
                                    controls={{
                                        // NOTE - disable export + search + apiTools for now
                                        exportData: {
                                            enabled: enableExport,
                                            exportTableName:
                                                ExportTableNames.assets_v4,
                                            exportFilename,
                                        },
                                        search: {
                                            enableBasic: enableBasicSearch,
                                            enablePro: enableProSearch,
                                            mode: SearchControlModes.Basic,
                                        },
                                        apiTools: {
                                            enabled: enableApiTools,
                                            endpoint: ApiQueryEndpoints.AssetV4,
                                        },
                                        queryExamples: {
                                            items: [],
                                            onSelectItem: (selectedExample) => {
                                                internalQuery.update(
                                                    selectedExample.query,
                                                );
                                            },
                                        },
                                        sharing: {
                                            enabled: false,
                                            url: appliedQuery.url,
                                        },
                                    }}
                                >
                                    <AssetTable
                                        rows={
                                            state.appliedQueryReq.data.results
                                        }
                                        loading={state.appliedQueryReq.loading}
                                        paginationState={
                                            state.appliedQueryReq.data
                                                .pagination
                                        }
                                        columns={columns}
                                        bordered={bordered}
                                        analyticsTableName={analyticsTableName}
                                        query={appliedQuery.query}
                                        onChangeQuery={appliedQuery.update}
                                        fixedHeader={fixedHeader}
                                        includeAdditionalNetworksRow={
                                            includeAdditionalNetworksRow
                                        }
                                        fixedHeaderClassName={
                                            fixedHeaderClassName
                                        }
                                    />
                                </ApiSearchLayout>
                            );
                        }}
                    </ApiQueryFetcher>
                )}
            </ApiQueryStateManager>
        </ClassnamePassthruWrapper>
    );
}
